import {
  Box,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  Button,
  Text,
  CircularProgress,
  CircularProgressLabel,
  Flex,
} from "@chakra-ui/react";
import MoneyField from "components/elements/MoneyInput";
import WidgetBox from "components/elements/WidgetBox/widget-box";
import { WidgetBoxActions } from "components/elements/WidgetBox/widget-box-actions";
import { useTenant } from "modules/tenant/providers/tenant-provider";
import { useEffect, useState } from "react";
import { apiRequest } from "utils/api";
import { fullNumberToString, numberToDollar, stringToFullNumber } from "utils/helpers";
import { getCustomButtonVariant } from "utils/uiconfighelper";
import { useLoansQuery } from "modules/loan/queries/use-loans-query";

const ExposureProgress = ({ progress, size }: { progress: number; size: string }) => {
  return (
    <CircularProgress value={progress} size={size} color="green.400">
      <CircularProgressLabel fontSize={size === "100%" ? "48px" : "16px"} fontWeight="500">
        {progress}%
      </CircularProgressLabel>
    </CircularProgress>
  );
};

const ExposureLimit = ({ company }: { company: any }) => {
  const { user } = useTenant();
  const [exposureLimit, setExposureLimit] = useState<number>(company.exposure_limit || 0);
  const [editExposure, setEditExposure] = useState("");
  const [amount, setAmount] = useState(0);
  const [loading, setLoading] = useState(false);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { data: loans = [] } = useLoansQuery();

  const updateCompany = async () => {
    try {
      setLoading(true);

      const exposure_limit = parseFloat(editExposure);

      await apiRequest("/supabase/companies/update", {
        id: company.id,
        values: {
          exposure_limit,
        },
      });
      setExposureLimit(exposure_limit);
      onClose();
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (company && loans.length > 0) {
      const filteredLoans = loans.filter((loan: any) => loan.borrower_company === company.id);
      setAmount(
        filteredLoans
          .filter((loan: any) => loan.status.isFunded)
          .reduce((acc: number, curr: any) => {
            return (acc += stringToFullNumber(curr.amount) || 0);
          }, 0)
      );
    }
  }, [company, loans]);

  useEffect(() => {
    setEditExposure(fullNumberToString(exposureLimit || 0));
  }, [exposureLimit]);

  return user.type === "lender" ? (
    <WidgetBox title="Exposure Limit">
      {exposureLimit > 0 && (
        <WidgetBoxActions>
          <Button onClick={onOpen} color="primary" variant="ghost" px="0px" size="sm">
            Edit
          </Button>
        </WidgetBoxActions>
      )}

      {exposureLimit ? (
        <Flex alignItems="center">
          <ExposureProgress progress={Math.round((amount / exposureLimit) * 100)} size="90px" />
          <Box ml="20px" w="100%">
            <Flex justifyContent="space-between" mb="8px">
              <Text fontWeight="600">Exposure Limit:</Text>
              <Text>{numberToDollar(exposureLimit)}</Text>
            </Flex>
            <Flex justifyContent="space-between">
              <Text fontWeight="600">Funded Loans:</Text>
              <Text>{numberToDollar(amount)}</Text>
            </Flex>
          </Box>
        </Flex>
      ) : (
        <Box textAlign="center">
          <Text mb="20px">You haven't set an exposure limit for this borrower company yet.</Text>
          <Button variant={getCustomButtonVariant(user)} onClick={onOpen}>
            Set Exposure Limit
          </Button>
        </Box>
      )}
      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent borderRadius="20px">
          <ModalHeader pb="10px">Set Exposure Limit</ModalHeader>
          <ModalCloseButton />
          <ModalBody pt="0px">
            <MoneyField
              value={editExposure}
              onValueChange={value => setEditExposure(value ?? "")}
            />
          </ModalBody>

          <ModalFooter>
            <Button
              variant={getCustomButtonVariant(user)}
              onClick={updateCompany}
              isLoading={loading}
            >
              Done
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </WidgetBox>
  ) : (
    <WidgetBox title="Exposure Limit">
      {company.exposure_limit ? (
        <ExposureProgress progress={Math.round((amount / exposureLimit) * 100)} size="100%" />
      ) : (
        <Text>Not enough data to calculate exposure limit.</Text>
      )}
    </WidgetBox>
  );
};

export default ExposureLimit;
